import { TFunction } from 'i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TagColorsMap } from './ParticipantItem.types';
import { Market, Presence, RegisterParticipantInput, Source } from 'generated/event.types';
import { testUserList } from 'modules/events/basics/constants/eventTesting.constants';
import { DeclinedParticipantStatus, JoiningChannel, ParticipantStatus, VeevaSyncStatus } from 'modules/events/basics/enums/events.enums';
import { RegistrationStatusHistory, Event } from 'modules/events/basics/types/events.types';
import { isDraft } from 'modules/events/basics/utils/events.utils';
import { findParticipantLaterStatus } from 'modules/events/basics/utils/participants.utils';
import { getE2EVipLink } from 'modules/events/components/EventTesting/EventTesting.utils';
import { useExportQrCodeMutation } from 'modules/events/graphql/mutations/exportQrCode';
import { Icons } from 'yoda-ui/components/Icons/Icon';
import { YodaColors } from 'yoda-ui/yodaTheme';

const tagColorsMap = (t: TFunction): TagColorsMap => ({
  [ParticipantStatus.accepted]: {
    label: t('participant_status_confirmed'),
    bgColor: YodaColors.greenSuccessLight,
    color: YodaColors.greenSuccessDark,
  },
  [ParticipantStatus.checkedIn]: {
    label: t('participant_status_checked_in'),
    bgColor: YodaColors.green,
    color: YodaColors.white,
  },
  [ParticipantStatus.invited]: {
    label: t('participant_status_invited'),
    bgColor: YodaColors.gray2,
    color: YodaColors.gray8,
  },
  [ParticipantStatus.requested]: {
    label: t('participant_status_pending'),
    bgColor: YodaColors.orangeWarningLight,
    color: YodaColors.orangeWarningDark,
  },
  [ParticipantStatus.rejected]: {
    label: t('participant_status_rejected'),
    bgColor: YodaColors.redErrorLight,
    color: YodaColors.redErrorDark,
  },
  [ParticipantStatus.waitingList]: {
    label: t('participant_status_waiting_list'),
    bgColor: YodaColors.orangeWarningLight,
    color: YodaColors.orangeWarningDark,
  },
  [DeclinedParticipantStatus.declined]: {
    label: t('participant_status_declined'),
    bgColor: YodaColors.redErrorLight,
    color: YodaColors.redErrorDark,
  },
});

export const veevaSyncStatusIconMap = {
  [VeevaSyncStatus.ERROR]: {
    icon: Icons.errorOutline,
  },
  [VeevaSyncStatus.SYNC]: {
    icon: Icons.taskAlt,
  },
  [VeevaSyncStatus.PENDING]: {
    icon: Icons.hourglass,
  },
  [VeevaSyncStatus.DELETED]: {
    icon: Icons.deletedForever,
  },
};

const useParticipantsItem = (
  registrationStatusHistory: RegistrationStatusHistory,
  userEmail: string,
  firstName: string,
  lastName: string,
  mdmId: string,
  e2eTestMode: boolean,
  event?: Event,
  veevaSyncStatus?: VeevaSyncStatus,
  syncParticipantHandler?: (registerParticipantInput: Partial<RegisterParticipantInput>) => Promise<boolean>,
  joiningChannel?: JoiningChannel,
) => {
  const { t } = useTranslation();
  const flags = useFlags();
  const [exportQrCode] = useExportQrCodeMutation();

  const [opened, setOpened] = useState(false);
  const [cancelModalopen, setCancelModalOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [isSigned, setIsSigned] = useState(false);

  const [isMoreActionsMenuVisible, setMoreActionMenuVisibile] = useState(false);

  const participantCurrentStatus = findParticipantLaterStatus(registrationStatusHistory);
  const isPublished = !isDraft(event?.status);
  const [isQrCodeGenerated, setIsQrCodeGenerated] = useState(false);

  const getUserVipLink = useCallback(async (eventStatus: string) => {
    const selectedTestUser = testUserList.find((user) => {
      return user.email === userEmail;
    });

    if (event && selectedTestUser) {
      const market = event?.data?.biogenLincConfig?.isSwitzerlandSpecific ? Market.ch : event?.market;
      const e2eUrl = await getE2EVipLink(
        selectedTestUser, event?.title, event?._id, market, flags.backofficeBiogenlinc5313GenerateVipLinks, isPublished,
      );
      if (e2eUrl) {
        const vipLink = new URL(e2eUrl);
        vipLink.searchParams.append('status', eventStatus);
        window.open(vipLink.toString(), '_blank');
      }
    }
  }, [event, userEmail, flags.backofficeBiogenlinc5313GenerateVipLinks, isPublished]);

  const handleGenerateQrCode = useCallback(async () => {
    const buffer = await exportQrCode(event?._id, userEmail);
    if (buffer) {
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${buffer}`;
      link.download = `${event?._id}_${lastName}_${firstName}.pdf`;
      link.click();
      toast.success(t('qr_code_generated_for', { name: `${firstName} ${lastName}` }));
    }
    setIsQrCodeGenerated(true);
  }, [event?._id, userEmail, firstName, lastName, exportQrCode, t]);

  const menuItemConfigs = useMemo(() => {
    const menu = [
      {
        key: t('participantsItem_moreActionsMenu_participantCancellation_label'),
        label: t('participantsItem_moreActionsMenu_participantCancellation_label'),
        onClick: () => { setCancelModalOpen(true); },
        hideItem: ParticipantStatus.rejected === participantCurrentStatus || ParticipantStatus.waitingList === participantCurrentStatus,
      },
      {
        key: t('participantsItem_moreActionsMenu_acceptParticipant_label'),
        label: t('participantsItem_moreActionsMenu_acceptParticipant_label'),
        onClick: () => { setAcceptModalOpen(true); },
        hideItem: ParticipantStatus.waitingList !== participantCurrentStatus,
      },

      {
        key: t('participantsItem_moreActionsMenu_testOngoingEvent_label'),
        label: t('participantsItem_moreActionsMenu_testOngoingEvent_label'),
        onClick: () => { getUserVipLink('ongoing'); },
        hideItem: !e2eTestMode,
      },
      {
        key: t('participantsItem_moreActionsMenu_testEndedEvent_label'),
        label: t('participantsItem_moreActionsMenu_testEndedEvent_label'),
        onClick: () => { getUserVipLink('ended'); },
        hideItem: !e2eTestMode,
      },
      {
        key: t('participantsItem_moreActionsMenu_registerAgainParticipant_label'),
        label: t('participantsItem_moreActionsMenu_registerAgainParticipant_label'),
        onClick: () => { setAcceptModalOpen(true); },
        hideItem: ParticipantStatus.rejected !== participantCurrentStatus,
      },
      {
        key: t('participantsItem_moreActionsMenu_checkInParticipant_label'),
        label: t('participantsItem_moreActionsMenu_checkInParticipant_label'),
        onClick: () => { setCheckInModalOpen(true); },
        hideItem: event?.presence === Presence.online
         || (event?.presence === Presence.hybrid && joiningChannel !== JoiningChannel.physical)
          || ParticipantStatus.accepted !== participantCurrentStatus,
      },

      {
        key: t('participantsItem_moreActionsMenu_generateQrCode_label'),
        label: t('participantsItem_moreActionsMenu_generateQrCode_label'),
        onClick: handleGenerateQrCode,
        hideItem: event?.presence === Presence.online
         || (event?.presence === Presence.hybrid && joiningChannel === JoiningChannel.online)
         || participantCurrentStatus === ParticipantStatus.invited
         || event?.data?.qrCodeRegistration === false,
      },
      {
        key: t('participantsItem_moreActionsMenu_collectSignature_label'),
        label: t('participantsItem_moreActionsMenu_collectSignature_label'),
        onClick: () => { setSignatureModalOpen(true); },
        hideItem: event?.presence === Presence.online
         || (event?.presence === Presence.hybrid && joiningChannel === JoiningChannel.online)
         || !flags.eventsCweb2287GatherParticipantSignatureForPhysicalAttendance
         || event?.data?.signatureCollection !== true,
      },
      {
        key: t('participantsItem_moreActionsMenu_syncParticipant_label'),
        label: t('participantsItem_moreActionsMenu_syncParticipant_label'),
        onClick: () => {
          if (syncParticipantHandler) {
            syncParticipantHandler({ mdmId, email: userEmail, eventId: event?._id, firstName, lastName });
          }
        },
        hideItem: !syncParticipantHandler || e2eTestMode || event?.source !== Source.veeva,
      },
    ];
    setMoreActionMenuVisibile(menu.some((item) => !item.hideItem));
    return menu;
  }, [
    e2eTestMode,
    event?._id,
    event?.presence,
    event?.source,
    firstName,
    getUserVipLink,
    handleGenerateQrCode,
    joiningChannel,
    lastName,
    mdmId,
    participantCurrentStatus,
    syncParticipantHandler,
    t,
    userEmail,
    flags.eventsCweb2287GatherParticipantSignatureForPhysicalAttendance,
    event?.data?.qrCodeRegistration,
    event?.data?.signatureCollection,
  ]);

  const toggleOpened = () => {
    setOpened((prev) => !prev);
  };

  const veevaSyncTagConfig = useMemo(
    () => {
      return veevaSyncStatus ? veevaSyncStatusIconMap[veevaSyncStatus] : veevaSyncStatusIconMap[VeevaSyncStatus.PENDING];
    },
    [veevaSyncStatus],
  );

  const tagConfig = useMemo(
    () => {
      const isDeclined = !!registrationStatusHistory.rejected && !registrationStatusHistory.accepted;

      const status = isDeclined ? DeclinedParticipantStatus.declined : findParticipantLaterStatus(registrationStatusHistory);
      return status ? tagColorsMap(t)[status] : null;
    },
    [registrationStatusHistory, t],
  );

  return {
    opened,
    tagConfig,
    veevaSyncTagConfig,
    toggleOpened,
    menuItemConfigs,
    cancelModalopen,
    setCancelModalOpen,
    acceptModalOpen,
    setAcceptModalOpen,
    isMoreActionsMenuVisible,
    checkInModalOpen,
    setCheckInModalOpen,
    isQrCodeGenerated,
    signatureModalOpen,
    setSignatureModalOpen,
    isSigned,
    setIsSigned,
    isSignatureEnabled: !!flags.eventsCweb2287GatherParticipantSignatureForPhysicalAttendance && event?.data?.signatureCollection === true,
    isQrCodeEnabled: event?.data?.qrCodeRegistration !== false,
    isInvitedParticipant: participantCurrentStatus === ParticipantStatus.invited,
  };
};

export default useParticipantsItem;
